import React, { useState } from 'react';
import ProviderServicesModal from '../../ProviderServicesModal';
import { useUnifiedAuth } from '../../../hooks/useUnifiedAuth';

const HealthcareProviderCard = ({ provider, searchQuery }) => {
  const [showAllServices, setShowAllServices] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const { user } = useUnifiedAuth();
  const isAdmin = user?.user_type === 'admin';

  const handleToggleListing = async () => {
    const isCurrentlyListed = provider.is_listed !== false;
    const action = isCurrentlyListed ? 'remove from website' : 'add to website';
    
    if (!window.confirm(`Are you sure you want to ${action} this provider?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('sessionToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/edit-user/${provider.user_id || provider.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_listed: !isCurrentlyListed })
      });

      if (response.ok) {
        alert(`✅ Successfully ${!isCurrentlyListed ? 'listed' : 'unlisted'} provider`);
        window.location.reload(); // Refresh to show changes
      } else {
        const errorData = await response.json();
        alert(`❌ Failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error toggling listing:', error);
      alert('❌ Error updating listing status');
    }
  };

  // Highlight search terms in text
  const highlightText = (text, query) => {
    if (!query || !text) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  // Get facility type icon and color
  const getFacilityInfo = (type) => {
    const types = {
      hospital: { icon: '🏥', color: 'text-red-600', bgColor: 'bg-red-50', label: 'Hospital' },
      clinic: { icon: '🏢', color: 'text-blue-600', bgColor: 'bg-blue-50', label: 'Medical Clinic' },
      urgent_care: { icon: '⚡', color: 'text-orange-600', bgColor: 'bg-orange-50', label: 'Urgent Care' },
      laboratory: { icon: '🔬', color: 'text-purple-600', bgColor: 'bg-purple-50', label: 'Laboratory' },
      pharmacy: { icon: '💊', color: 'text-green-600', bgColor: 'bg-green-50', label: 'Pharmacy' },
      medical_center: { icon: '🏛️', color: 'text-indigo-600', bgColor: 'bg-indigo-50', label: 'Medical Center' },
      specialty_clinic: { icon: '🩺', color: 'text-teal-600', bgColor: 'bg-teal-50', label: 'Specialty Clinic' }
    };
    
    return types[type] || types.clinic;
  };

  // Render rating stars
  const renderRating = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        <div className="flex">
          {[1, 2, 3, 4, 5].map((star) => (
            <svg
              key={star}
              className={`w-4 h-4 ${
                star <= rating ? 'text-yellow-400' : 'text-gray-300'
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
        <span className="text-sm text-gray-600">
          {rating} ({provider.total_reviews || 0} reviews)
        </span>
      </div>
    );
  };

  // Get operating hours status
  const getHoursStatus = () => {
    const now = new Date();
    const hour = now.getHours();
    
    if (hour >= 6 && hour < 22) {
      return { status: 'Open Now', color: 'text-green-600' };
    } else if (hour >= 22 || hour < 6) {
      return { status: '24/7 Emergency', color: 'text-blue-600' };
    } else {
      return { status: 'Closed', color: 'text-red-600' };
    }
  };

  const facilityInfo = getFacilityInfo(provider.facility_type);
  const hoursStatus = getHoursStatus();
  
  // Handle both string services and object services
  const rawServices = provider.services || [];
  const services = rawServices.map(service => {
    if (typeof service === 'string') {
      return service;
    } else if (typeof service === 'object' && service.name) {
      return service.name;
    }
    return service;
  });
  
  const displayServices = showAllServices ? services : services.slice(0, 6);

  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-shadow duration-200 overflow-hidden">
      <div className="p-6">
        {/* Header Section */}
        <div className="flex items-start space-x-4 mb-4">
          {/* Facility Logo/Image */}
          <div className="flex-shrink-0">
            {(() => {
              // Debug for Marmar only
              if (provider.name && provider.name.includes('Marmar')) {
                console.log('MARMAR RENDER CHECK:', {
                  hasLogo: !!provider.logo,
                  hasProfileImage: !!provider.profile_image,
                  hasImages: !!(provider.images && provider.images.length),
                  logoValue: provider.logo,
                  willShowImage: !!(provider.logo || provider.profile_image || (provider.images && provider.images.length > 0))
                });
              }
              return null;
            })()}
            {provider.logo || provider.profile_image || (provider.images && provider.images.length > 0) ? (
              <img
                src={provider.logo || provider.profile_image || (provider.images && provider.images[0])}
                alt={provider.name}
                className="w-16 h-16 rounded-lg border border-gray-200 object-cover"
              />
            ) : (
              <div className={`w-16 h-16 rounded-lg ${facilityInfo.bgColor} flex items-center justify-center text-2xl`}>
                {facilityInfo.icon}
              </div>
            )}
            
            {/* Verification Badge */}
            {provider.is_verified && (
              <div className="relative -mt-2 -mr-2 float-right">
                <div className="bg-green-500 rounded-full p-1">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Provider Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {highlightText(provider.name, searchQuery)}
                </h3>
                
                {/* Facility Type Badge */}
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${facilityInfo.bgColor} ${facilityInfo.color} mb-2`}>
                  <span className="mr-1">{facilityInfo.icon}</span>
                  {facilityInfo.label}
                </div>
                
                {/* Services For Badge */}
                {provider.services_for && (
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 mb-2 ${
                    provider.services_for === 'male' ? 'bg-blue-100 text-blue-700' :
                    provider.services_for === 'female' ? 'bg-pink-100 text-pink-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    <span className="mr-1">
                      {provider.services_for === 'male' && '👨'}
                      {provider.services_for === 'female' && '👩'}
                      {provider.services_for === 'mixed' && '👥'}
                    </span>
                    {provider.services_for === 'male' && 'Male Only'}
                    {provider.services_for === 'female' && 'Female Only'}
                    {provider.services_for === 'mixed' && 'Male & Female'}
                  </div>
                )}

                {/* Address */}
                {provider.address && (
                  <div className="flex items-start space-x-1 mt-1">
                    <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p className="text-sm text-gray-600">
                        {provider.address}
                        {provider.area && `, ${provider.area}`}
                        {provider.governorate && ` - ${provider.governorate}`}
                      </p>
                      {provider.google_maps_link && (
                        <a 
                          href={provider.google_maps_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center mt-1"
                        >
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          View on Google Maps
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Hours Status */}
              <div className="text-right">
                <div className={`text-sm font-medium ${hoursStatus.color}`}>
                  {hoursStatus.status}
                </div>
                <div className="text-xs text-gray-500">Hours vary</div>
              </div>
            </div>
          </div>
        </div>

        {/* Rating and Stats */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            {renderRating(provider.average_rating || 4.2)}
            {provider.total_doctors && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">{provider.total_doctors}</span> doctors
              </div>
            )}
          </div>
        </div>

        {/* Services */}
        {services.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Services Offered:</h4>
            <div className="flex flex-wrap gap-2">
              {displayServices.map((service, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                >
                  {highlightText(service, searchQuery)}
                </span>
              ))}
              
              {services.length > 6 && (
                <button
                  onClick={() => setShowAllServices(!showAllServices)}
                  className="px-3 py-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  {showAllServices ? 'Show less' : `+${services.length - 6} more`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Specialties */}
        {provider.specialities && provider.specialities.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Specialties:</h4>
            <div className="flex flex-wrap gap-2">
              {provider.specialities.map((specialty, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full font-medium"
                >
                  {highlightText(specialty, searchQuery)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Quick Info */}
        <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {provider.total_doctors || 'N/A'}
            </div>
            <div className="text-xs text-gray-500">Doctors</div>
          </div>
          
          <div className="text-center border-l border-gray-200">
            <div className="text-lg font-semibold text-gray-900">
              {services.length || 0}
            </div>
            <div className="text-xs text-gray-500">Services</div>
          </div>
          
          <div className="text-center border-l border-gray-200">
            <div className="text-lg font-semibold text-gray-900">
              {provider.is_emergency ? '24/7' : 'Standard'}
            </div>
            <div className="text-xs text-gray-500">Hours</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">
            Book Appointment
          </button>
          {/* Admin Controls */}
          {isAdmin && (
            <button 
              onClick={handleToggleListing}
              className={`px-4 py-2 border rounded-lg transition-colors font-medium ${
                provider.is_listed !== false 
                  ? 'border-orange-300 text-orange-700 hover:bg-orange-50' 
                  : 'border-green-300 text-green-700 hover:bg-green-50'
              }`}
              title={provider.is_listed !== false ? 'Remove from website' : 'Add to website'}
            >
              {provider.is_listed !== false ? '🚫 Unlist' : '✅ List'}
            </button>
          )}
          <button 
            onClick={() => setShowServicesModal(true)}
            className="px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors font-medium"
          >
            Services
          </button>
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </button>
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Services Modal */}
      <ProviderServicesModal
        provider={provider}
        isOpen={showServicesModal}
        onClose={() => setShowServicesModal(false)}
        onBookService={(service) => {
          // Navigate to provider booking page with selected service
          window.location.href = `/provider/${provider.id}/book?service=${service.id}`;
        }}
      />
    </div>
  );
};

export default HealthcareProviderCard;